
import Vue from 'vue';
import Component from 'vue-class-component';
import DollarInput from '@/core/components/DollarInput.vue';
import { namespace } from 'vuex-class';
import { LoanLinkedAccount } from '@/loan/types/loan-linked-account';
import { Prop } from 'vue-property-decorator';
import moment from 'moment';
import { AxiosError } from 'axios';
import { LoanDetails } from '@/loan/types/loan-details';
import { FORM_DEFAULT_SELECT_OPTION } from '@/core/constants/form-default-select-option';
import { FormSelectOption } from '@/core/types/form-select-option';
import { Alert } from '@/core/types/alert';
import { AlertTypeEnum } from '@/core/types/alert-type-enum';
import { LOAN_TAKE_BACK_MINIMUM_AMOUNT } from '@/loan/constants/loan-take-back-minimum-amount';
import { LoanCreateWithdrawalRequest } from '@/loan/types/loan-create-withdrawal-request';
import { LoanAPIService } from '@/loan/services/api';
import { LoanHelper } from '@/loan/helper';
import { CoreHelper } from '@/core/helper';
import { UtilHelper } from '@/util/helper';

const loanNamespace = namespace('loan');
const DEFAULT_ERROR_MESSAGE = 'There was an error submitting your Take-Back, please try again.';
const DEFAULT_DESCRIPTION = 'Take-Back';
const SUBMITTED_TRANSACTION_SUCCESS_MESSAGE = 'Take-Back Successful';
const TRANSACTION_AMOUNT_LESS_THAN_MINIMUM_TEXT = (amount: number) => `Your withdrawal must be at least ${UtilHelper.formatDollar(amount)}`;
const TRANSACTION_AMOUNT_MORE_THAN_MAXIMUM_TEXT = 'Your withdrawal must be at or below your available to Take-Back amount.';

@Component({
	components: {
		DollarInput,
	},
})
export default class LoanTakeBackConfirm extends Vue {
	public alert: Alert | null = null;

	@Prop({ required: true })
	public amount!: number;

	@loanNamespace.Getter('amountAvailableToTakeBack')
	public amountAvailableToTakeBack!: number;

	public descriptionValue: string = '';

	public accountOptions: FormSelectOption[] = [];

	public accountValue = FORM_DEFAULT_SELECT_OPTION.value;

	public formatDollar = UtilHelper.formatDollar;

	@loanNamespace.State('linkedAccounts')
	public linkedAccounts!: LoanLinkedAccount[];

	@loanNamespace.State('loanDetails')
	public loanDetails!: LoanDetails;

	@loanNamespace.Getter('loanId')
	public loanId!: string;

	public isTransactionLoading = false;

	public transactionAmountValue = 0;

	public wasTransactionSuccessful = false;

	get cutOffDateForDisplay(): string {
		const { cutoffDate } = this.loanDetails.oneTimePayment;
		const date = new Date(cutoffDate);
		return UtilHelper.formatDateForDisplay(date);
	}

	get cutOffTime(): string {
		const { cutoffDate } = this.loanDetails.oneTimePayment;
		return moment(cutoffDate).format('hh:mm A');
	}

	get description(): string {
		return (this.descriptionValue && this.descriptionValue.length)
			? this.descriptionValue
			: DEFAULT_DESCRIPTION;
	}

	get effectiveDateForDisplay(): string {
		return UtilHelper.formatISOStringForDisplay(this.effectiveDateISOString);
	}

	get effectiveDateISOString(): string {
		return this.loanDetails.oneTimePayment.effectiveDate;
	}

	get isTransactionSubmitButtonDisabled(): boolean {
		const isFromAccountValid = this.accountValue !== FORM_DEFAULT_SELECT_OPTION.value;
		return !isFromAccountValid || (!this.isTransactionAmountMoreThanMin || !this.isTransactionAmountLessThanMax);
	}

	get isTransactionAmountLessThanMax(): boolean {
		return this.transactionAmountValue <= this.amountAvailableToTakeBack;
	}

	get isTransactionAmountMoreThanMin(): boolean {
		return this.transactionAmountValue >= LOAN_TAKE_BACK_MINIMUM_AMOUNT;
	}

	get transactionAmountInputErrorMessage(): string | null {
		if (!this.isTransactionAmountMoreThanMin) {
			return TRANSACTION_AMOUNT_LESS_THAN_MINIMUM_TEXT(LOAN_TAKE_BACK_MINIMUM_AMOUNT);
		}

		if (!this.isTransactionAmountLessThanMax) {
			return TRANSACTION_AMOUNT_MORE_THAN_MAXIMUM_TEXT;
		}

		return null;
	}

	get transactionAmountInputHint(): string {
		return `Maximum: ${UtilHelper.formatDollar(this.amountAvailableToTakeBack)}`;
	}

	mounted(): void {
		this.accountOptions = LoanHelper.getLinkedAccountsSelectOtions(this.linkedAccounts, FORM_DEFAULT_SELECT_OPTION);

		// auto select if only account AND verified
		if (this.linkedAccounts.length === 1 && this.linkedAccounts[0].verified) {
			this.accountValue = this.accountOptions[1]?.value;
		} else {
			this.accountValue = FORM_DEFAULT_SELECT_OPTION.value;
		}

		this.transactionAmountValue = this.amount;
	}

	public async onSubmitTransaction(): Promise<void> {
		this.alert = null;
		this.isTransactionLoading = true;

		const request: LoanCreateWithdrawalRequest = {
			account: this.accountValue as string,
			amount: this.transactionAmountValue,
			description: this.description,
			effective_date: this.effectiveDateISOString,
		};

		await this.createWithdrawal(request);
	}

	public linkNewAccount() {
		this.$router.push({ name: 'ProfileMyProfile', params: { modalIsOpen: 'true', paymentRefUrl: window.location.href } });
	}

	public checkIsAccountDisabled(accountOption: FormSelectOption): boolean {
		const linkedAccount = this.linkedAccounts.find((account) => account.id === accountOption.value);

		if (!linkedAccount) {
			return true;
		}

		return !linkedAccount.verified;
	}

	private async createWithdrawal(request: LoanCreateWithdrawalRequest): Promise<void> {
		try {
			await LoanAPIService.createWithdrawal(this.loanId, request);
			this.wasTransactionSuccessful = true;

			this.alert = {
				type: AlertTypeEnum.SUCCESS,
				message: SUBMITTED_TRANSACTION_SUCCESS_MESSAGE,
			};
		} catch (error) {
			const axiosError = error as AxiosError;
			const validatedError = CoreHelper.validateAxiosError(
				axiosError,
				DEFAULT_ERROR_MESSAGE,
			);

			this.alert = {
				type: AlertTypeEnum.ERROR,
				message: validatedError.message,
			};

			this.wasTransactionSuccessful = false;
		} finally {
			this.isTransactionLoading = false;
		}
	}
}

